import { Build, Component, Element, Prop, State, h } from '@stencil/core';
import { v4 } from 'uuid';
import type { LtcInputZipCustomEvent, LtcSwitchCustomEvent } from '~/components';
import type { ICarpentryComponent, IColorTheme, TInsuranceProduct, TInsuranceType, TInsuranceTypeListItem } from '~/interfaces';
import { type IWidgetTracking, WidgetClicked, WidgetLoaded, WidgetUsed, callUniqueEntry } from '~/util/analytics';
import { InsuranceAge, InsuranceType } from '~/util/constants';
import { MaxLength } from '~/util/decorators';
import { parseAndAddUrlParams } from '~/util/fetch';

const HEADER_LIMIT = 60 as const;
const CTA_LIMIT = 25 as const;
const FOOTER_LIMIT = 35 as const;

const FOOTER_TEXT = `It's free, simple and secure.` as const;
const CTA_TEXT = 'Find Insurers' as const;
const HEADER_TEXT_AUTO = 'Find the Cheapest Auto Insurance Quotes in Your Area' as const;
const HEADER_TEXT_HOME = 'Find the Cheapest Home Insurance Quotes in Your Area' as const;
const HEADER_TEXT_RENTER = 'Find the Cheapest Renters Insurance Quotes in Your Area' as const;

@Component({
  tag: 'ltc-lead-insurance',
  styleUrl: 'insurance-leadbox.less',
  shadow: true,
})
export class InsuranceLeadbox implements ICarpentryComponent {
  /*
   * 1. Own Properties
   */
  id = v4();

  private insuranceTypeListData: Record<string, TInsuranceTypeListItem> = {};

  /*
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLLtcLeadInsuranceElement;

  /*
   * 3. State() variables
   */
  @State() age: number;
  @State() cValue: string;
  @State() insuranceProductValue: TInsuranceProduct;
  @State() insuranceTypeValue: TInsuranceType;
  @State() isInsured = true;
  @State() vValue = 'LendingTree';
  @State() zipCode: string;
  @State() zipIsValidating = false;

  /*
   * 4. Public Property API
   */

  /**
   * Hide Insurance Type dropdown select. The `insuranceType` prop will still have effect on the component.
   */
  @Prop({ reflect: true }) hideInsuranceType: boolean;

  /**
   * Default value for Insurance Type dropdown select
   */
  @Prop({ reflect: true }) insuranceType: TInsuranceType = 'auto';

  /**
   * If `true` will render header as H1-like style (DM-Serif), otherwise H3-like (Lato).
   */
  @Prop({ reflect: true }) useHeadlineFont: boolean;

  /**
   * Leadbox color theme
   */
  @Prop({ reflect: true }) theme: IColorTheme = 'white';

  /**
   * Leadbox header. Set to override default value, max 60 symbols
   */
  @MaxLength(HEADER_LIMIT)
  @Prop({ reflect: true })
  header: string;

  /**
   * Use this setting to override default footer icon.
   * For available icons see the `ltc-icon` documentation.
   */
  @Prop({ reflect: true }) footerIcon: string;

  /**
   * Use this setting to override default footer text, max 35 symbols
   */
  @MaxLength(FOOTER_LIMIT)
  @Prop({ reflect: true })
  footerText: string;

  /**
   * Use this setting to override default CTA button text, max 25 symbols
   */
  @MaxLength(CTA_LIMIT)
  @Prop({ reflect: true })
  ctaText: string;

  /**
   * If `true`, will hide the footer on the Leadbox
   */
  @Prop({ reflect: true }) hideFooter: boolean;

  /**
   * If `true`, open the CTA action in a new browser window, if not (default) - in the same window.
   */
  @Prop({ reflect: true }) newWindow: boolean;

  /**
   * Any extra parameters that you want to add to a splitter-id URL for CTA button.
   * <br/>
   * Example: `product=renter&AndMore=SomethingElse`
   */
  @Prop({ reflect: true }) withQueryParam: string;

  /**
   * Set `true` to add ZIP input filed
   */
  @Prop({ reflect: true }) showZipInput: boolean;

  /*
   * Prop lifecycle events
   */

  /*
   * 5. Events section
   */

  /*
   * 6. Component lifecycle events
   */
  constructor() {
    for (const insuranceType of InsuranceType) {
      this.insuranceTypeListData[insuranceType.type] = insuranceType;
    }

    this.insuranceTypeValue = this.insuranceType;

    this.setRouterValues();
  }

  componentDidLoad(): void {
    WidgetLoaded(this, this.getTrackingProps());
  }

  componentWillLoad(): void {
    if (Build.isServer) {
      return;
    }
  }

  /*
   * 7. Listeners
   */

  /*
   * 8. Public methods API
   */

  /*
   * 9. Local methods
   */
  callTrackingOnce() {
    callUniqueEntry(this.id, () => WidgetUsed(this, this.getTrackingProps()));
  }

  private setRouterValues = () => {
    this.cValue = this.insuranceTypeListData[this.insuranceTypeValue].c;
    this.insuranceProductValue = this.insuranceTypeListData[this.insuranceTypeValue].product;
  };

  private getHeaderText = () => {
    if (this.header) {
      return this.header;
    }

    switch (this.insuranceTypeValue) {
      case 'auto':
        return HEADER_TEXT_AUTO;
      case 'home':
        return HEADER_TEXT_HOME;
      case 'renter':
        return HEADER_TEXT_RENTER;
    }
  };

  private getCtaText = () => {
    return this.ctaText || CTA_TEXT;
  };

  private getFooterText = () => {
    return this.footerText || FOOTER_TEXT;
  };

  private getFooterIcon = () => {
    if (this.footerIcon) {
      return this.footerIcon;
    }

    switch (this.insuranceTypeValue) {
      case 'auto':
        return 'auto';
      case 'home':
        return 'mortgage';
      case 'renter':
        return 'rent';
    }
  };

  private onTypeChange = (e: Event) => {
    this.callTrackingOnce();

    this.insuranceTypeValue = (e.target as HTMLSelectElement).value as TInsuranceType;

    this.setRouterValues();
  };

  private onAgeChange = (e: Event) => {
    this.callTrackingOnce();

    this.age = Number.parseInt((e.target as HTMLSelectElement).value);
  };

  private onSwitchChange = ({ detail }: LtcSwitchCustomEvent<boolean>) => {
    this.callTrackingOnce();
    this.isInsured = detail;
  };

  private redirectToSplitter = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();

    const url = new URL(REPLACE_INSURANCE_ROUTER);

    if (this.zipCode && this.showZipInput) {
      url.searchParams.set('zip', this.zipCode);
    }
    if (this.age) {
      url.searchParams.set('age', String(this.age));
    }

    url.searchParams.set('v', this.vValue);
    url.searchParams.set('c', this.cValue);
    url.searchParams.set('product', this.insuranceProductValue);
    url.searchParams.set('Insurance Type', this.insuranceTypeValue);
    url.searchParams.set('Prior Insurance Status', this.isInsured ? '1' : '0');
    url.searchParams.set('key', `_${location.pathname.split('/').filter(Boolean).join('_') || ''}`);

    parseAndAddUrlParams(url, this.withQueryParam);

    WidgetClicked(this, this.getTrackingProps());

    window.open(url, this.newWindow ? '_blank' : '_self');
  };

  getTrackingProps = (): IWidgetTracking => {
    return {
      footer_displayed: !this.hideFooter,
      footer_icon: this.getFooterIcon(),
      footer_text: this.getFooterText(),
      headline_tag: this.useHeadlineFont ? 'h1' : 'h3',
      headline_text: this.getHeaderText(),
      theme: this.theme,
      product: `${this.insuranceProductValue}/${this.insuranceTypeValue}`,
      extra_query_param: this.withQueryParam,
    };
  };

  private onLtZipValidating = ({ detail }: LtcInputZipCustomEvent<boolean>) => {
    this.zipIsValidating = detail;
  };

  private onLtZipChange = ({ detail }: LtcInputZipCustomEvent<string>) => {
    if (this.zipCode) {
      this.callTrackingOnce();
    }
    this.zipCode = detail;
  };

  /*
   * 10. render() function
   */
  render() {
    const { hideFooter, theme, useHeadlineFont, zipIsValidating, insuranceTypeValue, age, isInsured, hideInsuranceType, showZipInput } =
      this;

    return (
      <article class={{ container: true, [`theme-${theme}`]: true }}>
        <header class={{ header: true, h1: useHeadlineFont, h3: !useHeadlineFont }}>{this.getHeaderText()}</header>
        <form
          onSubmit={(e) => e.preventDefault()}
          class={{ 'form-container': true, 'no-type-select': hideInsuranceType, 'no-zip-input': !showZipInput }}
        >
          {hideInsuranceType ? null : (
            <label class={{ 'insurance-type select-with-label': true, 'select-with-label--empty': !insuranceTypeValue }}>
              <span>Insurance Type</span>
              <select name="InsuranceType" onInput={this.onTypeChange}>
                <option value="" disabled selected={!insuranceTypeValue}>
                  Insurance Type
                </option>
                {InsuranceType.map(({ label, id, type }) => {
                  return (
                    // color is for Chrome on Windows specific
                    <option style={{ color: '#101f30' }} key={id} value={type} selected={type === insuranceTypeValue}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <ltc-icon name="chevron-down" aria-label="Select" />
            </label>
          )}
          {showZipInput ? <ltc-input-zip class="zip-code" onLtChange={this.onLtZipChange} onLtValidating={this.onLtZipValidating} /> : null}

          <label class={{ 'age select-with-label': true, 'select-with-label--empty': !age }}>
            <span>Age</span>
            <select name="Age" onInput={this.onAgeChange}>
              <option value="" disabled selected={!age}>
                Age
              </option>
              {InsuranceAge.map(({ label, value }) => {
                return (
                  // color is for Chrome on Windows specific
                  <option style={{ color: '#101f30' }} key={label} value={value} selected={value === age}>
                    {label}
                  </option>
                );
              })}
            </select>
            <ltc-icon name="chevron-down" aria-label="Select" />
          </label>
          <ltc-switch checked={isInsured} theme={theme} labelPosition={'left'} alignment="start" onLtcChange={this.onSwitchChange}>
            Currently Insured?
          </ltc-switch>
          <button
            type="button"
            tabindex="0"
            class="cta-btn"
            disabled={zipIsValidating}
            onClick={this.redirectToSplitter}
            onKeyPress={this.redirectToSplitter}
          >
            {this.getCtaText()}
          </button>
        </form>
        {hideFooter ? null : (
          <footer>
            <ltc-icon name={this.getFooterIcon()} aria-label="Footer Icon" size="small" />
            {this.getFooterText()}
          </footer>
        )}
      </article>
    );
  }
}
